/** Marketing i18n fragment — ucApprovedReplies (use-cases/freshdesk-jira-approved-replies). */

export const ucApprovedRepliesEn = {
  shot: {
    label: "Support issues",
    alt: "Support tickets in FixControl from Slack and email, triaged with priority and governed actions before anything is sent.",
  },
  meta: {
    title: "Approve exactly what gets sent to Freshdesk, Jira, Odoo or TOPdesk",
    description:
      "Draft support replies with AI, edit the text, and choose where it goes: a Freshdesk customer reply, an internal note, a Jira comment or a chat thread. Nothing is sent until a person approves it, and every send is recorded.",
  },
  hero: {
    eyebrow: "Approved replies",
    title: "Draft with AI, approve exactly what gets sent to",
    highlight: "Freshdesk, Jira, Odoo or TOPdesk",
    subtitle:
      "Answering a customer of your organization well takes searching, drafting and polishing. FixControl proposes the reply — in your house style, with the signature, template and tone you've set. Someone on your team edits it, picks the destination and approves it; only then does anything leave your own organization. Every send is recorded in the approval ledger.",
    primaryCta: "Book a demo",
    secondaryCta: "Talk to us",
    bullet1: "Human-approved sends",
    bullet2: "A named target on every reply",
    bullet3: "Every send in the ledger",
  },
  how: {
    eyebrow: "How it works",
    title: "Propose, edit, approve — then it sends",
    description:
      "A draft is a starting point. The person on your team who approves the reply controls both the wording and where it goes.",
  },
  steps: {
    propose: {
      tag: "AI proposes",
      title: "FixControl drafts a candidate answer",
      body: "FixControl drafts a reply from the ticket, its history and the knowledge sources your project is bound to. At this point it is only a draft, and nothing has left your own organization.",
    },
    edit: {
      tag: "Human edits",
      title: "You review and edit the wording",
      body: "Someone on your team reads the proposed text and edits it freely. What gets dispatched is the edited text: the approval applies to your final wording.",
    },
    target: {
      tag: "Pick the target",
      title: "Choose where it goes",
      body: "Select the destination for each reply: a Freshdesk customer reply, a Freshdesk internal note, a Jira comment, an Odoo customer reply, a TOPdesk customer reply, a Slack or Teams thread, or a FixControl issue comment. Customer-visible and internal targets are kept distinct.",
    },
    approve: {
      tag: "Approve to send",
      title: "Approve, and only then does it leave",
      body: "On approval, the final edited text is dispatched through the outbox to the chosen target. The decision is recorded in the approval ledger, with the approver, their role and the channel.",
    },
  },
  features: {
    eyebrow: "What you get",
    title: "Control over wording, audience and delivery",
    description: "Each reply carries its own decision record, destination and delivery state.",
    draft: {
      title: "Edit before anything sends",
      body: "The AI proposal is a draft. Someone on your team revises and approves it, and only that signed-off text is dispatched.",
    },
    target: {
      title: "A named destination on every send",
      body: "Every send states where it goes: customer reply, internal note, Jira comment, chat thread or FixControl comment. Because customer-visible targets are separate from internal ones, replying to a customer of your organization is always a deliberate selection.",
    },
    ledger: {
      title: "A decision record on every send",
      body: "Each approved reply records who approved it, the final edited text, the chosen target and what happened on delivery.",
    },
    outbox: {
      title: "Outbox, retry and dead-letter",
      body: "External writes route through an outbox. Transient failures retry automatically; a write that keeps failing lands in a visible dead-letter state where you can act on it.",
    },
    health: {
      title: "Per-capability provider health",
      body: "FixControl tracks each provider capability separately. If Freshdesk can be read but replies cannot be posted, you see that before you depend on the write path.",
    },
    noSilent: {
      title: "No unapproved customer replies",
      body: "Freshdesk, Odoo and TOPdesk replies and Jira comments always require a human approval; auto-send is not offered on these channels. For email, auto-send is off by default and exists only as an opt-in, policy-bounded low-risk mode, with every send audit-logged.",
    },
  },
  targets: {
    eyebrow: "Reply targets",
    title: "Where an approved reply can go",
    description:
      "Each destination shows its own capability-level status. Customer-visible and internal targets are separate paths with separate labels.",
  },
  capabilities: {
    freshdeskReply: {
      name: "Freshdesk customer reply",
      body: "Send an approved, customer-visible reply to a Freshdesk ticket after a person signs off on the final wording.",
    },
    freshdeskNote: {
      name: "Freshdesk internal note",
      body: "Post an internal note on a Freshdesk ticket, visible to agents and hidden from the customer. Notes are a separate target from public replies.",
    },
    jiraComment: {
      name: "Jira internal comment",
      body: "Add an approved comment to a Jira issue for your team. On Jira Software projects, comments are internal collaboration and are not customer-facing.",
    },
    jsmReply: {
      name: "Jira Service Management customer reply",
      body: "Customer-visible replies apply only to Jira Service Management request types. Jira Software projects have no customer reply channel, so this depends on your Jira configuration.",
    },
    chatThread: {
      name: "Slack / Teams thread",
      body: "Post the approved text back into the Slack or Teams thread where your team is working on the ticket.",
    },
    autoSend: {
      name: "Auto-send without approval",
      body: "Not offered for Freshdesk replies or Jira comments. A customer reply or comment on these channels goes out only after a human approval.",
    },
  },
  governed: {
    eyebrow: "The default is a human decision",
    title: "Customer replies ship with a recorded decision",
    noteTitle: "Approval, outbox and delivery",
    noteBody:
      "By default, nothing is sent without a recorded human decision; the only autonomy is the opt-in, policy-bounded email mode, and it is audit-logged. External writes route through an outbox with retries and a visible dead-letter state. Provider health is tracked per capability, so a degraded write path shows up before you rely on it.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Questions support teams ask",
    q1: {
      q: "Can a reply go out without anyone approving it?",
      a: "Not on these channels. Freshdesk, Odoo and TOPdesk replies and Jira comments always require a human approval before dispatch. The only auto-send anywhere in FixControl is an opt-in, policy-bounded email mode, off by default and audit-logged.",
    },
    q2: {
      q: "Can a customer of your organization accidentally receive an internal note?",
      a: "The reply target is selected per message, and internal notes and customer replies are distinct targets with distinct labels. Sending to your customer is a deliberate selection, not a default.",
    },
    q3: {
      q: "Does every Jira project have a customer reply channel?",
      a: "No. Customer-visible replies apply to Jira Service Management request types. On Jira Software projects, comments are internal team collaboration, and FixControl labels the capability accordingly.",
    },
    q4: {
      q: "What is recorded when a reply is sent?",
      a: "The approver, their role, the channel they approved from, the timestamp, the final edited text, the chosen target, the evidence shown and the delivery outcome.",
    },
    q5: {
      q: "What happens if the external write fails?",
      a: "Outbound writes go through an outbox with retries. A write that keeps failing lands in a dead-letter state that is visible in the timeline, and the reply is not marked as sent.",
    },
    q6: {
      q: "How do I know a provider can actually receive the reply?",
      a: "FixControl reports health per capability. If Freshdesk, Jira, Odoo or TOPdesk can be read but a write capability is degraded, the status shows it before you depend on that path.",
    },
  },
  related: {
    ledger: {
      label: "AI approval ledger",
      description: "How every AI-assisted action gets a decision record.",
    },
    freshdesk: {
      label: "Freshdesk integration",
      description: "Customer replies, internal notes and ticket mirroring.",
    },
    jira: {
      label: "Jira integration",
      description: "Comments and transitions, with per-capability health.",
    },
  },
  cta: {
    title: "See the approved reply flow",
    description:
      "Book a demo and send a governed reply yourself: draft, edit, pick a target and approve.",
    primary: "Book a demo",
    secondary: "Talk to us",
  },
};

export const ucApprovedRepliesNl = {
  shot: {
    label: "Supportissues",
    alt: "Supporttickets in FixControl uit Slack en e-mail, getrieerd met prioriteit en gegoverneerde acties voordat er iets wordt verzonden.",
  },
  meta: {
    title: "Keur precies goed wat er naar Freshdesk, Jira, Odoo of TOPdesk wordt verstuurd",
    description:
      "Stel supportantwoorden op met AI, bewerk de tekst en kies waar het heen gaat: een Freshdesk-klantantwoord, een interne notitie, een Jira-opmerking of een chat-thread. Er wordt niets verstuurd voordat een mens het goedkeurt, en elke verzending wordt vastgelegd.",
  },
  hero: {
    eyebrow: "Goedgekeurde antwoorden",
    title: "Stel op met AI, keur precies goed wat er wordt verstuurd naar",
    highlight: "Freshdesk, Jira, Odoo of TOPdesk",
    subtitle:
      "Een klant van je organisatie goed beantwoorden kost zoekwerk, schrijfwerk en polijstwerk. FixControl stelt het antwoord voor — in jouw huisstijl, met de handtekening, template en toon die jij hebt ingesteld. Iemand van je team bewerkt het, kiest de bestemming en keurt het goed; pas dan verlaat er iets je eigen organisatie. Elke verzending wordt vastgelegd in het goedkeuringsgrootboek.",
    primaryCta: "Boek een demo",
    secondaryCta: "Neem contact op",
    bullet1: "Door mensen goedgekeurde verzendingen",
    bullet2: "Een benoemde bestemming op elk antwoord",
    bullet3: "Elke verzending in het grootboek",
  },
  how: {
    eyebrow: "Hoe het werkt",
    title: "Voorstellen, bewerken, goedkeuren — dan verstuurt het",
    description:
      "Een concept is een vertrekpunt. Wie uit je team het antwoord goedkeurt, bepaalt zowel de formulering als de bestemming.",
  },
  steps: {
    propose: {
      tag: "AI stelt voor",
      title: "FixControl stelt een kandidaat-antwoord op",
      body: "Op basis van het ticket, de historie ervan en de kennis waaraan je project is gekoppeld, stelt FixControl een antwoord voor. Het is een concept — er verlaat nog niets je eigen organisatie.",
    },
    edit: {
      tag: "Mens bewerkt",
      title: "Je beoordeelt en bewerkt de formulering",
      body: "Iemand van je team leest de voorgestelde tekst en bewerkt die vrij. Wat wordt verstuurd, is de bewerkte tekst: de goedkeuring geldt voor jouw definitieve formulering.",
    },
    target: {
      tag: "Kies de bestemming",
      title: "Kies waar het naartoe gaat",
      body: "Selecteer de bestemming voor elk antwoord: een Freshdesk-klantantwoord, een interne notitie in Freshdesk, een Jira-opmerking, een Odoo-klantantwoord, een TOPdesk-klantantwoord, een Slack- of Teams-thread, of een opmerking op een FixControl-issue. Klantzichtbare en interne bestemmingen worden gescheiden gehouden.",
    },
    approve: {
      tag: "Goedkeuren om te versturen",
      title: "Keur goed, en pas dan vertrekt het",
      body: "Bij goedkeuring wordt de definitief bewerkte tekst via de outbox naar de gekozen bestemming verzonden. De beslissing wordt vastgelegd in het goedkeuringsgrootboek, met de goedkeurder, zijn rol en het kanaal.",
    },
  },
  features: {
    eyebrow: "Wat je krijgt",
    title: "Grip op formulering, publiek en aflevering",
    description: "Elk antwoord draagt zijn eigen beslissingsrecord, bestemming en afleverstatus.",
    draft: {
      title: "Bewerk voordat er iets wordt verstuurd",
      body: "Het AI-voorstel is een concept. Iemand van je team herziet en keurt het goed, en alleen die afgetekende tekst wordt verstuurd.",
    },
    target: {
      title: "Een benoemde bestemming op elke verzending",
      body: "Elke verzending benoemt waar het heen gaat: klantantwoord, interne notitie, Jira-opmerking, chat-thread of FixControl-opmerking. Omdat klantzichtbare bestemmingen los staan van interne, is antwoorden aan een klant van je organisatie altijd een bewuste keuze.",
    },
    ledger: {
      title: "Een beslissingsrecord bij elke verzending",
      body: "Elk goedgekeurd antwoord legt vast wie het goedkeurde, de definitief bewerkte tekst, de gekozen bestemming en wat er bij aflevering gebeurde.",
    },
    outbox: {
      title: "Outbox, retry en dead-letter",
      body: "Externe writes lopen via een outbox. Tijdelijke fouten worden automatisch opnieuw geprobeerd; een write die blijft falen belandt in een zichtbare dead-letter-status waarop je kunt handelen.",
    },
    health: {
      title: "Capability-health van providers",
      body: "FixControl volgt elke provider-capability apart. Als Freshdesk wel gelezen maar geen antwoorden geplaatst kunnen worden, zie je dat voordat je op het write-pad vertrouwt.",
    },
    noSilent: {
      title: "Geen ongekeurde klantantwoorden",
      body: "Freshdesk-, Odoo- en TOPdesk-antwoorden en Jira-opmerkingen vereisen altijd een menselijke goedkeuring; automatisch versturen wordt op deze kanalen niet aangeboden. Voor e-mail staat automatisch versturen standaard uit en bestaat het alleen als een opt-in, policy-begrensde laag-risicomodus, met elke verzending audit-logged.",
    },
  },
  targets: {
    eyebrow: "Antwoordbestemmingen",
    title: "Waar een goedgekeurd antwoord naartoe kan",
    description:
      "Elke bestemming toont zijn eigen status op capability-niveau. Klantzichtbare en interne bestemmingen zijn gescheiden paden met gescheiden labels.",
  },
  capabilities: {
    freshdeskReply: {
      name: "Freshdesk customer reply",
      body: "Verstuur een goedgekeurd, klantzichtbaar antwoord naar een Freshdesk-ticket nadat een mens de definitieve formulering heeft afgetekend.",
    },
    freshdeskNote: {
      name: "Freshdesk internal note",
      body: "Plaats een interne notitie op een Freshdesk-ticket, zichtbaar voor agents en verborgen voor de klant. Notities zijn een aparte bestemming ten opzichte van publieke antwoorden.",
    },
    jiraComment: {
      name: "Jira internal comment",
      body: "Voeg een goedgekeurde opmerking toe aan een Jira-issue voor je team. Op Jira Software-projecten zijn opmerkingen interne samenwerking en niet klantgericht.",
    },
    jsmReply: {
      name: "Jira Service Management customer reply",
      body: "Klantzichtbare antwoorden gelden alleen voor Jira Service Management-aanvraagtypes. Op Jira Software-projecten is er geen klantantwoordkanaal, dus dit is afhankelijk van je Jira-configuratie.",
    },
    chatThread: {
      name: "Slack / Teams thread",
      body: "Plaats de goedgekeurde tekst terug in de Slack- of Teams-thread waar je team aan het ticket werkt.",
    },
    autoSend: {
      name: "Auto-send without approval",
      body: "Niet aangeboden voor Freshdesk-antwoorden of Jira-opmerkingen. Een klantantwoord of opmerking op deze kanalen gaat pas uit na een menselijke goedkeuring.",
    },
  },
  governed: {
    eyebrow: "De standaard is een menselijke beslissing",
    title: "Klantantwoorden gaan uit met een vastgelegde beslissing",
    noteTitle: "Goedkeuring, outbox en aflevering",
    noteBody:
      "Standaard wordt er niets verstuurd zonder een vastgelegde menselijke beslissing; de enige autonomie is de opt-in, policy-begrensde e-mailmodus, en die is audit-logged. Externe writes lopen via een outbox met retries en een zichtbare dead-letter-status. Provider-health wordt per capability gevolgd, zodat een gedegradeerd write-pad zichtbaar wordt voordat je erop vertrouwt.",
  },
  faq: {
    eyebrow: "FAQ",
    title: "Vragen die supportteams stellen",
    q1: {
      q: "Kan een antwoord uitgaan zonder dat iemand het goedkeurt?",
      a: "Niet op deze kanalen. Freshdesk-, Odoo- en TOPdesk-antwoorden en Jira-opmerkingen vereisen altijd een menselijke goedkeuring voordat ze uitgaan. De enige automatische verzending in heel FixControl is een opt-in, policy-begrensde e-mailmodus, standaard uit en audit-logged.",
    },
    q2: {
      q: "Kan een klant van je organisatie per ongeluk een interne notitie ontvangen?",
      a: "De antwoordbestemming wordt per bericht gekozen, en interne notities en klantantwoorden zijn aparte bestemmingen met aparte labels. Versturen aan je klant is een bewuste keuze, geen standaard.",
    },
    q3: {
      q: "Heeft elk Jira-project een klantantwoordkanaal?",
      a: "Nee. Klantzichtbare antwoorden gelden voor Jira Service Management-aanvraagtypes. Op Jira Software-projecten zijn opmerkingen interne teamsamenwerking, en FixControl labelt de capability daarnaar.",
    },
    q4: {
      q: "Wat wordt er vastgelegd wanneer een antwoord wordt verstuurd?",
      a: "De goedkeurder, zijn rol, het kanaal van waaruit hij goedkeurde, het tijdstip, de definitief bewerkte tekst, de gekozen bestemming, het getoonde bewijs en de afleveruitkomst.",
    },
    q5: {
      q: "Wat gebeurt er als de externe write faalt?",
      a: "Uitgaande writes lopen via een outbox met retries. Een write die blijft falen belandt in een dead-letter-status die zichtbaar is in de tijdlijn, en het antwoord wordt niet als verzonden gemarkeerd.",
    },
    q6: {
      q: "Hoe weet ik dat een provider het antwoord daadwerkelijk kan ontvangen?",
      a: "FixControl rapporteert health per capability. Als Freshdesk, Jira, Odoo of TOPdesk wel gelezen kan worden maar een write-capability gedegradeerd is, toont de status dat voordat je op dat pad vertrouwt.",
    },
  },
  related: {
    ledger: {
      label: "AI-goedkeuringsgrootboek",
      description: "Hoe elke AI-ondersteunde actie een beslissingsrecord krijgt.",
    },
    freshdesk: {
      label: "Freshdesk-integratie",
      description: "Klantantwoorden, interne notities en het spiegelen van tickets.",
    },
    jira: {
      label: "Jira-integratie",
      description: "Opmerkingen en transities, met capability-health.",
    },
  },
  cta: {
    title: "Bekijk de flow voor goedgekeurde antwoorden",
    description:
      "Boek een demo en verstuur zelf een gegoverneerd antwoord: opstellen, bewerken, een bestemming kiezen en goedkeuren.",
    primary: "Boek een demo",
    secondary: "Neem contact op",
  },
};
